import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { useAuth } from "@/contexts/useAuth";
import { apiDelete, apiGet, apiPatch, apiPost, getApiErrorMessage } from "@/api";
import { toDisplayString } from "@/lib/utils";
import { tokenQuery } from "./query-options";
import type { QueryKey } from "@tanstack/react-query";

export interface ReminderItem {
  id: string;
  userId: string;
  captureId: string | null;
  title: string;
  note: string | null;
  remindAt: string;
  completed: boolean;
  createdAt: string;
}

export interface CreateReminderInput {
  title: string;
  note?: string;
  remindAt: string;
  captureId?: string | null;
}

export interface RemindersQueryData {
  reminders: ReminderItem[];
  premiumRequired: boolean;
}

/**
 * `includeCompleted` is part of the key because it changes which reminders come back — sharing
 * one key would serve the open list where the full one was asked for, and vice versa.
 */
export function remindersQueryOptions(token: string | null, includeCompleted = false) {
  return tokenQuery(["reminders", includeCompleted], token, async (t) => {
    const suffix = includeCompleted ? "?completed=true" : "";
    const res = await apiGet<{ reminders?: ReminderItem[] }>(`/reminders${suffix}`, t);
    if (!res.ok) {
      const code = (res.error as { error?: { code?: string } } | undefined)?.error?.code;
      if (res.status === 403 && code === "premium_required") {
        return { reminders: [], premiumRequired: true } satisfies RemindersQueryData;
      }
      throw new Error(getApiErrorMessage(res, "Reminders konnten nicht geladen werden"));
    }
    return { reminders: res.data.reminders ?? [], premiumRequired: false } satisfies RemindersQueryData;
  });
}

export function useReminders(includeCompleted = false) {
  const { state } = useAuth();
  const token = state.status === "authenticated" ? state.token : null;
  return useQuery(remindersQueryOptions(token, includeCompleted));
}

/**
 * All three reminder mutations clear the same list they were fired from — including
 * `includeCompleted`, because that flag is part of the key. Dropping it from the invalidate key
 * while leaving it in the query key is the exact drift this indirection removes: the mutation
 * would clear a list nobody is looking at and leave a deleted reminder on screen.
 */
export function remindersInvalidateKeys(token: string | null, includeCompleted = false): readonly QueryKey[] {
  return [remindersQueryOptions(token, includeCompleted).queryKey];
}

export function useCreateReminder(includeCompleted = false) {
  const { state } = useAuth();
  const token = state.status === "authenticated" ? state.token : null;
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (input: CreateReminderInput) => {
      if (!token) throw new Error("Nicht angemeldet");
      const res = await apiPost("/reminders", token, input);
      if (!res.ok) throw new Error(getApiErrorMessage(res, "Reminder konnte nicht erstellt werden"));
      return res.data;
    },
    onSuccess: () => {
      for (const queryKey of remindersInvalidateKeys(token, includeCompleted)) {
        void queryClient.invalidateQueries({ queryKey });
      }
    },
  });
}

export function useUpdateReminder(includeCompleted = false) {
  const { state } = useAuth();
  const token = state.status === "authenticated" ? state.token : null;
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async ({
      id,
      completed,
    }: {
      id: string;
      completed: boolean;
    }) => {
      if (!token) throw new Error("Nicht angemeldet");
      const res = await apiPatch(`/reminders/${encodeURIComponent(toDisplayString(id))}`, token, {
        completed,
      });
      if (!res.ok) throw new Error(getApiErrorMessage(res, "Reminder konnte nicht aktualisiert werden"));
      return res.data;
    },
    onSuccess: () => {
      for (const queryKey of remindersInvalidateKeys(token, includeCompleted)) {
        void queryClient.invalidateQueries({ queryKey });
      }
    },
  });
}

export function useDeleteReminder(includeCompleted = false) {
  const { state } = useAuth();
  const token = state.status === "authenticated" ? state.token : null;
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (id: string) => {
      if (!token) throw new Error("Nicht angemeldet");
      const res = await apiDelete(`/reminders/${encodeURIComponent(toDisplayString(id))}`, token);
      if (!res.ok) throw new Error(getApiErrorMessage(res, "Reminder konnte nicht gelöscht werden"));
      return res;
    },
    onSuccess: () => {
      for (const queryKey of remindersInvalidateKeys(token, includeCompleted)) {
        void queryClient.invalidateQueries({ queryKey });
      }
    },
  });
}
